import type { HexColor } from '@miyuki/types';

import { ValidationError } from '../errors';

import { MAX_COLORS, MIN_COLORS } from './pattern.constants';

/**
 * Ángulo áureo. Repartir los matices con él evita que dos colores contiguos de
 * la paleta se parezcan, cosa que sí ocurre al dividir 360° en partes iguales.
 */
const GOLDEN_ANGLE = 137.508;

/** Pastel: saturación media y claridad alta. */
const SATURATION = 0.62;
const LIGHTNESS_STEPS = [0.8, 0.72, 0.64] as const;

function toChannel(value: number): string {
  return Math.round(value * 255)
    .toString(16)
    .padStart(2, '0');
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Valida y normaliza un color a `#rrggbb` en minúsculas. */
export function normalizeHexColor(value: string): HexColor {
  if (!HEX_COLOR.test(value)) {
    throw new ValidationError('El color debe tener el formato #rrggbb', [
      { path: 'color', message: `Recibido: ${value}` },
    ]);
  }
  return value.toLowerCase() as HexColor;
}

/** Conversión HSL → `#rrggbb`. `hue` en grados, el resto en [0, 1]. */
export function hslToHex(hue: number, saturation: number, lightness: number): HexColor {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = (((hue % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((sector % 2) - 1));
  const offset = lightness - chroma / 2;

  const [red, green, blue] = (
    [
      [chroma, second, 0],
      [second, chroma, 0],
      [0, chroma, second],
      [0, second, chroma],
      [second, 0, chroma],
      [chroma, 0, second],
    ] as const
  )[Math.floor(sector) % 6] ?? [0, 0, 0];

  return `#${toChannel(red + offset)}${toChannel(green + offset)}${toChannel(blue + offset)}` as HexColor;
}

/**
 * Paleta inicial de `count` pasteles distintos entre sí.
 *
 * Es determinista a propósito: el mismo tamaño da siempre los mismos colores,
 * de modo que dos diseños creados igual arrancan igual y la función se prueba
 * sin sorpresas. Son solo valores de partida: la usuaria puede reemplazar
 * cualquiera por cualquier color del espacio RGB.
 */
export function defaultPalette(count: number): readonly HexColor[] {
  if (!Number.isInteger(count) || count < MIN_COLORS || count > MAX_COLORS) {
    throw new ValidationError(
      `La cantidad de colores debe ser un entero entre ${MIN_COLORS} y ${MAX_COLORS}`,
      [{ path: 'colorCount', message: `Recibido: ${count}` }],
    );
  }

  return Array.from({ length: count }, (_unused, index) =>
    hslToHex(
      index * GOLDEN_ANGLE,
      SATURATION,
      LIGHTNESS_STEPS[index % LIGHTNESS_STEPS.length] ?? LIGHTNESS_STEPS[0],
    ),
  );
}
